import { BOT_CONV_MESSAGE_TYPE, SELF_CONV_MESSAGE_TYPE } from '../../consts/common-consts';

export function createMessagePayload({
  senderType,
  type,
  messageId,
  message,
  metadata,
  createdAt,
}) {
  return { senderType, type, messageId, message, metadata, createdAt };
}

/**
 * Pull the display text out of a stored user message.
 *
 * Live turns are composed here as plain strings, but the ones replayed from
 * `/sessions/{id}/messages` come back in the same `{type, content}` envelope
 * the bot uses - and `content` is itself sometimes an object carrying the text
 * under `displayText`/`text`. Anything that cannot be reduced to a string is
 * rendered as JSON rather than handed to React as a bare object.
 */
function toDisplayText(input) {
  if (input === null || input === undefined) {
    return '';
  }
  if (typeof input === 'string') {
    return input;
  }
  if (typeof input !== 'object') {
    return String(input);
  }

  const candidate =
    input.content !== undefined
      ? input.content
      : input.displayText ?? input.text ?? input.caption ?? input.inputText;

  if (typeof candidate === 'string') {
    return candidate;
  }
  if (candidate && typeof candidate === 'object') {
    return toDisplayText(candidate);
  }

  return JSON.stringify(input);
}

export function createSelfMessagePayload(inputText, createdAt, messageId) {
  return createMessagePayload({
    senderType: SELF_CONV_MESSAGE_TYPE,
    type: 'TEXT',
    messageId: messageId || 'user',
    message: toDisplayText(inputText),
    createdAt,
  });
}

export function createBotMessagePayload(message, createdAt, messageId) {
  // A message that arrives in an unrecognised shape is still rendered, as raw
  // content, rather than dropped - same fallback the web-plugin uses.
  if (!message || !message.type) {
    message = { type: 'default', content: message, metadata: {} };
  }

  return createMessagePayload({
    senderType: BOT_CONV_MESSAGE_TYPE,
    type: message.type,
    messageId: messageId || 'bot',
    message: message.content,
    metadata: message.metadata,
    createdAt,
  });
}

/**
 * Best-effort one-line label for a session in the history rail.
 *
 * Bot payloads are polymorphic (cards, buttons, images), so a plain string is
 * only available for text turns; everything else falls back to its type.
 */
export function summarizeMessage(message) {
  if (typeof message === 'string') {
    return message;
  }
  if (!message || typeof message !== 'object') {
    return '';
  }
  if (typeof message.content === 'string') {
    return message.content;
  }
  const content = message.content || message;
  return content.displayText || content.caption || content.text || '';
}
